import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { adminApi } from "../../lib/adminApi";
import { toast } from "sonner";
import { Send, Mail } from "lucide-react";
import DOMPurify from "dompurify";

const TIERS = ["all", "free", "premium", "platinum"];

const TEMPLATES = [
  { name: "Welcome", subject: "Welcome to Care for Flora 🌱", body: "<h2>Hi {{first_name}},</h2><p>Welcome to Care for Flora! Tap below to identify your first plant.</p><a href=\"/identify\">Identify a plant</a>" },
  { name: "Upgrade nudge", subject: "Unlock plant remedies with Premium", body: "<h2>{{first_name}}, your plants deserve more</h2><p>Upgrade to Premium and get up to 5 disease remedies, country-specific care, and zero ads.</p>" },
  { name: "Re-engagement", subject: "Your plants miss you 🌿", body: "<h2>Come back to your collection</h2><p>It's been a while, {{first_name}}. New features are live — explore them now.</p>" },
];

export default function EmailCampaignsScreen() {
  const [form, setForm] = useState({ subject: "", body_html: "", target_tier: "all", target_country: "" });
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await adminApi.get("/campaigns?channel=email");
      setHistory(data.campaigns || []);
    } catch (err) {
      console.error("[EmailCampaigns] load failed:", err);
    }
  };
  useEffect(() => { load(); }, []);

  // Sanitize the live preview HTML to prevent XSS. Only allow safe tags.
  const sanitizedPreview = useMemo(() => {
    const raw = form.body_html || "<em>Body preview…</em>";
    return DOMPurify.sanitize(raw, {
      ALLOWED_TAGS: [
        "h1", "h2", "h3", "h4", "p", "a", "b", "i", "em", "strong",
        "ul", "ol", "li", "br", "span", "div", "img",
      ],
      ALLOWED_ATTR: ["href", "src", "alt", "title", "target", "rel"],
      ALLOW_DATA_ATTR: false,
    });
  }, [form.body_html]);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form };
      if (!body.target_country) delete body.target_country;
      const { data } = await adminApi.post("/notifications/email", body);
      toast.success(`Email scheduled for ${data.delivered_count} recipients (mocked send)`);
      setForm({ subject: "", body_html: "", target_tier: "all", target_country: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <AdminLayout>
      <h1 className="text-2xl font-bold text-shadow-green mb-1">Email Campaigns</h1>
      <p className="text-muted-soft text-sm mb-6">Compose, target and dispatch transactional/marketing emails.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <form onSubmit={send} className="glass-card-strong p-6" data-testid="email-form">
            <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-3">Compose</p>

            <div className="flex flex-wrap gap-2 mb-4">
              {TEMPLATES.map((t) => (
                <button key={t.name} type="button" onClick={() => setForm({ ...form, subject: t.subject, body_html: t.body })} className="glass-pill text-[11px]" data-testid={`tmpl-${t.name.toLowerCase().replace(' ', '-')}`}>
                  {t.name}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-soft">Subject (max 60)</label>
                <input className="glass-input mt-1" maxLength={60} required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="email-subject" />
              </div>
              <div>
                <label className="text-xs text-muted-soft">Body (HTML supported, merge tags: {`{{first_name}}`}, {`{{tier}}`})</label>
                <textarea className="glass-input mt-1 font-mono text-[12px]" rows={8} required value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} data-testid="email-body" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-soft">Target tier</label>
                  <select className="glass-input mt-1" value={form.target_tier} onChange={(e) => setForm({ ...form, target_tier: e.target.value })} data-testid="email-target-tier">
                    {TIERS.map((t) => <option key={t} value={t} style={{ background: "#0A0A0A" }}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-soft">Country (optional)</label>
                  <input className="glass-input mt-1" value={form.target_country} onChange={(e) => setForm({ ...form, target_country: e.target.value })} />
                </div>
              </div>
            </div>

            <button type="submit" disabled={busy} className="btn-primary w-full mt-5 inline-flex items-center justify-center gap-2" data-testid="email-send">
              <Send size={14} /> {busy ? "Sending…" : "Send campaign"}
            </button>
            <p className="text-[10px] text-muted-soft text-center mt-2">Email delivery is MOCKED in v1 — campaigns are recorded only.</p>
          </form>

          {/* Preview */}
          <div className="glass-card p-4 mt-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-soft mb-3">Live preview</p>
            <div className="rounded-lg p-4 bg-white text-black text-sm" data-testid="email-preview">
              <p className="font-semibold mb-2">{form.subject || "Subject preview"}</p>
              <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizedPreview }} />
            </div>
          </div>
        </div>

        <div className="glass-card overflow-hidden">
          <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
            <Mail size={14} className="text-[#4CAF50]" />
            <p className="text-sm font-semibold">Recent email campaigns</p>
          </div>
          <div className="max-h-[700px] overflow-y-auto">
            {history.length === 0 ? (
              <p className="p-8 text-center text-soft text-sm">No campaigns yet.</p>
            ) : history.map((c) => (
              <div key={c.campaign_id} className="px-4 py-3 border-b border-white/3 last:border-b-0" data-testid={`email-history-${c.campaign_id}`}>
                <div className="flex items-start gap-2">
                  <p className="text-sm font-semibold flex-1 truncate">{c.subject}</p>
                  <span className="glass-pill text-[10px]">{c.target_tier}</span>
                </div>
                <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-soft">
                  <span>📨 {c.delivered_count} recipients</span>
                  <span>·</span>
                  <span>{new Date(c.created_at).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
